
  import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Button } from 'react-bootstrap';

const API_URL = 'https://api.sifgroup.org/api/v1/articles?limit=20&offset=0';

const formatDate = (value) => {
  if (!value) return 'Recently';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return 'Recently';
  }

  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  }).format(date);
};
0

const ArticleDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchArticle = async () => {
      try {
        const response = await fetch(API_URL);
        if (!response.ok) throw new Error('Failed to fetch article details');

        const data = await response.json();
        const publishedArticles = Array.isArray(data)
          ? data.filter((article) => article.status === 'published')
          : [];

        const article = publishedArticles.find((entry) => String(entry.id) === String(id));
        const sortedArticles = [...publishedArticles].sort((a, b) => {
          const aDate = new Date(a.published_at || a.created_at || 0).getTime();
          const bDate = new Date(b.published_at || b.created_at || 0).getTime();
          return bDate - aDate;
        });

        setPost(article || null);
        setRelatedPosts(sortedArticles.filter((entry) => String(entry.id) !== String(id)).slice(0, 2));
      } catch (error) {
        console.error('Error fetching article details:', error);
        setPost(null);
        setRelatedPosts([]);
      } finally {
        setLoading(false);
      }
    };

    fetchArticle();
  }, [id]);

  if (loading) {
    return (
      <Container className="section-padding text-center">
        <Row>
          <Col lg={8} className="mx-auto">
            <h2 className="mb-4">Loading article...</h2>
          </Col>
        </Row>
      </Container>
    );
  }

  if (!post) {
    return (
      <Container className="section-padding text-center">
        <Row>
          <Col lg={8} className="mx-auto">
            <h2 className="mb-4">Article Not Found</h2>
            <p className="mb-4" style={{ color: '#666' }}>
              The article you're looking for doesn't exist or has been moved.
            </p>
            <Button onClick={() => navigate('/articles')} className="btn-gold">
              Back to Articles
            </Button>
          </Col>
        </Row>
      </Container>
    );
  }

  const shareUrl = window.location.origin + window.location.pathname;
  const shareTitle = post.title;
  const shareText = `Check out this article from Golf4 International: ${shareTitle}`;

  const articleContent = post.content && /<\/?[a-z][\s\S]*>/i.test(post.content)
    ? post.content
    : (post.content || '').replace(/\n{2,}/g, '\n\n');

  return (
    <>
      <section style={{
        height: '60vh',
        background: `linear-gradient(rgba(31, 61, 43, 0.8), rgba(31, 61, 43, 0.9)), url(${post.featured_image || 'https://images.unsplash.com/photo-1535131749006-b7f58c99034b'}) center/cover no-repeat`,
        display: 'flex',
        alignItems: 'center'
      }}>
        <Container>
          <Row>
            <Col lg={10} className="text-white">
              <div className="mb-3">
                <span className="badge rounded-pill px-3 py-2" style={{
                  backgroundColor: 'var(--muted-gold)',
                  fontSize: '0.9rem'
                }}>
                  {post.topic || 'Golf'}
                </span>
              </div>
              <h1 className="display-4 fw-bold mb-4" style={{ color: '#B59A5A' }}>{post.title}</h1>
              <div className="d-flex align-items-center flex-wrap gap-2">
                <span>{formatDate(post.published_at || post.created_at)}</span>
                <span>•</span>
                <span>{post.read_time || 5} min read</span>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      <section className="section-padding">
        <Container>
          <Row>
            <Col lg={8} className="mx-auto">
              <div className="d-flex align-items-center mb-5 p-4 bg-soft-sage rounded">
                <div style={{
                  width: '60px',
                  height: '60px',
                  backgroundColor: 'white',
                  borderRadius: '50%',
                  marginRight: '1.5rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <span style={{ fontSize: '1.5rem', color: 'var(--muted-gold)' }}>👤</span>
                </div>
                <div>
                  <h4 className="h5 mb-1">{post.author || 'Golf4International Editorial Team'}</h4>
                  <p style={{ color: '#666' }} className="mb-0">
                    Experts in golf technology, coaching, and industry trends
                  </p>
                </div>
              </div>

              <div className="blog-content mb-5" style={{
                color: '#333',
                lineHeight: '1.8',
                fontSize: '1.1rem'
              }}>
                {articleContent.includes('<') ? (
                  <div dangerouslySetInnerHTML={{ __html: articleContent }} />
                ) : (
                  <div style={{ whiteSpace: 'pre-line' }}>{articleContent}</div>
                )}
              </div>

              {/* Share Buttons */}
              {/* Share Buttons */}
              <div className="border-top border-bottom py-4 mb-5">
                <h5 className="h6 mb-3">Share this article:</h5>

                <div className="d-flex flex-wrap gap-2">

                  {/* Facebook */}
                  <button
                    className="btn-gold"
                    style={{ padding: "8px 20px" }}
                    onClick={() =>
                      window.open(
                        `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
                        "_blank"
                      )
                    }
                  >
                    <i className="fab fa-facebook me-2"></i>Facebook
                  </button>

                  {/* LinkedIn */}
                  <button
                    className="btn-gold"
                    style={{ padding: "8px 20px" }}
                    onClick={() =>
                      window.open(
                        `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
                        "_blank"
                      )
                    }
                  >
                    <i className="fab fa-linkedin me-2"></i>LinkedIn
                  </button>

                  {/* Twitter / X */}
                  <button
                    className="btn-gold"
                    style={{ padding: "8px 20px" }}
                    onClick={() =>
                      window.open(
                        `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`,
                        "_blank"
                      )
                    }
                  >
                    <i className="fab fa-twitter me-2"></i>Twitter
                  </button>

                  {/* WhatsApp */}
                  <button
                    className="btn-gold"
                    style={{ padding: "8px 20px" }}
                    onClick={() =>
                      window.open(
                        `https://api.whatsapp.com/send?text=${encodeURIComponent(shareText + " " + shareUrl)}`,
                        "_blank"
                      )
                    }
                  >
                    <i className="fab fa-whatsapp me-2"></i>WhatsApp
                  </button>

                </div>
              </div>
              {/* <div className="border-top border-bottom py-4 mb-5">
                <h5 className="h6 mb-3">Share this article:</h5>
                <div className="d-flex flex-wrap gap-2">
                  <button className="btn-gold" style={{ padding: '8px 20px' }}>
                    <i className="fab fa-twitter me-2"></i>Twitter
                  </button>
                  <button className="btn-gold" style={{ padding: '8px 20px' }}>
                    <i className="fab fa-facebook me-2"></i>Facebook
                  </button>
                  <button className="btn-gold" style={{ padding: '8px 20px' }}>
                    <i className="fab fa-linkedin me-2"></i>LinkedIn
                  </button>
                  <button className="btn-gold" style={{ padding: '8px 20px' }}>
                    <i className="fas fa-envelope me-2"></i>Email
                  </button>
                </div>
              </div> */}

              {/* Navigation */}
              <div className="d-flex justify-content-between mb-5">
                <Button
                  onClick={() => navigate('/articles')}
                  className="btn-gold"
                >
                  ← Back to Articles
                </Button>
                <Button
                  onClick={() => navigate('/contact')}
                  className="btn-gold-filled"
                >
                  Contact Us →
                </Button>
              </div>
            </Col>
          </Row>

          {/* Related Posts */}
          {relatedPosts.length > 0 && (
            <>
              <Row className="mb-4">
                <Col>
                  <h3 className="h4">Related Articles</h3>
                </Col>
              </Row>

              <Row className="g-4">
                {relatedPosts.map((relatedPost) => (
                  <Col md={6} key={relatedPost.id}>
                    <div
                      className="card-modern h-100 border-0"
                      onClick={() => navigate(`/articles/${relatedPost.id}`)}
                      style={{ cursor: 'pointer' }}
                    >
                      <div
                        style={{
                          height: '200px',
                          background: `url(${relatedPost.featured_image || relatedPost.imageUrl || 'https://images.unsplash.com/photo-1535131749006-b7f58c99034b'}) center/cover no-repeat`
                        }}
                      />
                      <div className="p-4">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <small className="text-muted">{formatDate(relatedPost.published_at || relatedPost.created_at)}</small>
                          <small className="text-muted">{relatedPost.read_time || 5} min read</small>
                        </div>
                        <h4 className="h5 mb-3">{relatedPost.title}</h4>
                        <p className="text-muted mb-0">{relatedPost.excerpt || relatedPost.seo_description}</p>
                      </div>
                    </div>
                  </Col>
                ))}
              </Row>
            </>
          )}
        </Container>
      </section>

      {/* Newsletter CTA */}
      <section className="section-padding bg-soft-sage">
        <Container>
          <Row>
            <Col lg={8} className="mx-auto text-center">
              <h3 className="h4 mb-3">Never Miss an Update</h3>
              <p style={{ color: '#666' }} className="mb-4">
                Subscribe to our newsletter for regular golf insights, tips, and exclusive content.
              </p>
              <div className="input-group mx-auto" style={{ maxWidth: '500px' }}>
                <input
                  type="email"
                  className="form-control"
                  placeholder="Enter your email"
                  style={{ padding: '12px' }}
                />
                <button className="btn-gold-filled" type="button">
                  Subscribe
                </button>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </>
  );
};

export default ArticleDetail;